import { Geotab, Group } from "@fairfleet/geotab";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { COMPANY_GROUP_ID } from "@/constants/geotabGroupIds";
import {
  GROUP_PRESETS,
  GROUP_TEMPLATES,
  GroupNode,
} from "@/constants/groupTemplates";
import { useGroups } from "@/hooks/groups/useGroups";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GroupStatus {
  name: string;
  exists: boolean;
  groupId?: string;
  children: GroupStatus[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Merge multiple template node trees so that shared ancestors
 * (matched by name) are combined rather than duplicated.
 */
function mergeNodeTrees(trees: GroupNode[][]): GroupNode[] {
  const merged = new Map<string, GroupNode>();

  for (const tree of trees) {
    for (const node of tree) {
      const existing = merged.get(node.name);
      if (existing) {
        // Merge children recursively
        const combinedChildren = mergeNodeTrees([
          existing.children ?? [],
          node.children ?? [],
        ]);
        merged.set(node.name, { ...existing, children: combinedChildren });
      } else {
        merged.set(node.name, { ...node });
      }
    }
  }

  return Array.from(merged.values());
}

/**
 * Walk the template tree and resolve each node against existing Geotab groups
 * to determine which groups already exist and which need to be created.
 */
function resolveGroupStatus(
  nodes: GroupNode[],
  parentId: string,
  groupsByParent: Map<string, Group[]>,
): GroupStatus[] {
  return nodes.map(node => {
    const siblings = groupsByParent.get(parentId) ?? [];
    const match = siblings.find(g => g.name === node.name);

    const childStatuses = node.children
      ? resolveGroupStatus(
          node.children,
          match?.id ?? "",
          groupsByParent,
        )
      : [];

    return {
      name: node.name,
      exists: !!match,
      groupId: match?.id,
      children: childStatuses,
    };
  });
}

/** Count nodes that need to be created (exists === false). */
function countMissing(statuses: GroupStatus[]): number {
  let count = 0;
  for (const s of statuses) {
    if (!s.exists) count++;
    count += countMissing(s.children);
  }
  return count;
}

/** Count total nodes in the tree. */
function countTotal(statuses: GroupStatus[]): number {
  let count = 0;
  for (const s of statuses) {
    count++;
    count += countTotal(s.children);
  }
  return count;
}

/**
 * Recursively create groups that don't exist yet.
 * Parents are created before children so we can use their returned IDs.
 */
async function createGroupTree(
  statuses: GroupStatus[],
  parentId: string,
  geotabClient: Geotab,
  onCreated: (name: string) => void,
): Promise<void> {
  for (const status of statuses) {
    let nodeId = status.groupId;

    if (!status.exists) {
      const result = await geotabClient.call("Add", {
        typeName: "Group",
        entity: {
          name: status.name,
          comments: "",
          parent: { id: parentId },
          isGlobalReportingGroup: false,
          color: { r: 0, g: 0, b: 0, a: 255 },
        },
      });
      nodeId = (result as string) ?? undefined;
      onCreated(status.name);
    }

    if (status.children.length > 0 && nodeId) {
      await createGroupTree(status.children, nodeId, geotabClient, onCreated);
    }
  }
}

// ---------------------------------------------------------------------------
// Recursive tree row renderer
// ---------------------------------------------------------------------------

function GroupStatusRows({ statuses, depth }: { statuses: GroupStatus[]; depth: number }) {
  const { t } = useTranslation();

  return (
    <>
      {statuses.map(status => (
        <Box key={`${depth}-${status.name}`}>
          <ListItem sx={{ pl: 2 + depth * 3 }} dense>
            <ListItemIcon sx={{ minWidth: 32 }}>
              {status.exists ? (
                <CheckCircleIcon fontSize="small" color="success" />
              ) : (
                <RadioButtonUncheckedIcon fontSize="small" color="action" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={status.name}
              secondary={
                status.exists
                  ? t("admin.groups.exists")
                  : t("admin.groups.willCreate")
              }
              slotProps={{
                secondary: {
                  color: status.exists ? "success.main" : "text.secondary",
                },
              }}
            />
          </ListItem>
          {status.children.length > 0 && (
            <GroupStatusRows statuses={status.children} depth={depth + 1} />
          )}
        </Box>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function GroupBootstrapManager() {
  const { t } = useTranslation();
  const { geotabClient } = useGeotabClient();
  const queryClient = useQueryClient();
  const { data: allGroups, isLoading: groupsLoading } = useGroups();

  const [selectedPreset, setSelectedPreset] = useState(GROUP_PRESETS[0]?.id ?? "");
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(
    () => new Set(GROUP_PRESETS[0]?.templateIds ?? []),
  );
  const [creating, setCreating] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // When preset changes, update selected templates
  const handlePresetChange = useCallback((presetId: string) => {
    setSelectedPreset(presetId);
    const preset = GROUP_PRESETS.find(p => p.id === presetId);
    if (preset) {
      setSelectedTemplateIds(new Set(preset.templateIds));
    }
  }, []);

  const handleTemplateToggle = useCallback((templateId: string) => {
    setSelectedTemplateIds(prev => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
    // Clear preset selection since user customized
    setSelectedPreset("");
  }, []);

  // Build parent→children lookup from all groups.
  // Geotab groups expose children arrays, not parent refs, so we index
  // by walking each group's children and mapping childId → full Group object.
  const groupsByParent = useMemo(() => {
    const map = new Map<string, Group[]>();
    if (!allGroups) return map;
    const groupById = new Map(allGroups.map(g => [g.id, g]));
    for (const g of allGroups) {
      if (g.children) {
        const resolved: Group[] = [];
        for (const child of g.children) {
          const full = groupById.get(child.id);
          if (full) resolved.push(full);
        }
        if (resolved.length > 0) {
          map.set(g.id, resolved);
        }
      }
    }
    return map;
  }, [allGroups]);

  // Find CompanyGroup ID
  const companyGroup = useMemo(
    () => allGroups?.find(g => g.id === COMPANY_GROUP_ID) ?? null,
    [allGroups],
  );

  // Merge selected templates and resolve status
  const resolvedTree = useMemo(() => {
    if (!companyGroup || !allGroups) return [];

    const selectedTemplates = GROUP_TEMPLATES.filter(t =>
      selectedTemplateIds.has(t.id),
    );
    const mergedNodes = mergeNodeTrees(selectedTemplates.map(t => t.nodes));
    return resolveGroupStatus(mergedNodes, companyGroup.id, groupsByParent);
  }, [companyGroup, allGroups, selectedTemplateIds, groupsByParent]);

  const missing = useMemo(() => countMissing(resolvedTree), [resolvedTree]);
  const total = useMemo(() => countTotal(resolvedTree), [resolvedTree]);

  const handleCreate = async () => {
    if (!companyGroup) return;
    setCreating(true);
    setCreatedCount(0);
    setError(null);
    setSuccessMessage(null);

    try {
      await createGroupTree(resolvedTree, companyGroup.id, geotabClient, () => {
        setCreatedCount(prev => prev + 1);
      });

      // Invalidate group queries so the UI refreshes
      await queryClient.invalidateQueries({ queryKey: ["group"] });
      await queryClient.invalidateQueries({ queryKey: ["groups"] });

      setSuccessMessage(t("admin.groups.createSuccess"));
    } catch (e) {
      setError(e instanceof Error ? e.message : t("admin.groups.createError"));
    } finally {
      setCreating(false);
    }
  };

  if (groupsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Preset selector */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <TextField
          select
          size="small"
          label={t("admin.groups.preset")}
          value={selectedPreset}
          onChange={e => handlePresetChange(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">
            <em>{t("admin.groups.customSelection")}</em>
          </MenuItem>
          {GROUP_PRESETS.map(p => (
            <MenuItem key={p.id} value={p.id}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>

        <Button
          variant="contained"
          size="small"
          onClick={handleCreate}
          disabled={creating || missing === 0 || selectedTemplateIds.size === 0}
        >
          {creating
            ? t("admin.groups.creating")
            : missing === 0
              ? t("admin.groups.allExist")
              : t("admin.groups.createMissing", { count: missing })}
        </Button>
      </Box>

      {/* Template checkboxes */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        {GROUP_TEMPLATES.map(tmpl => (
          <FormControlLabel
            key={tmpl.id}
            control={
              <Checkbox
                checked={selectedTemplateIds.has(tmpl.id)}
                onChange={() => handleTemplateToggle(tmpl.id)}
                size="small"
              />
            }
            label={tmpl.label}
          />
        ))}
      </Box>

      {/* Progress bar during creation */}
      {creating && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={missing > 0 ? (createdCount / missing) * 100 : 0}
          />
          <Typography variant="caption" sx={{ mt: 0.5 }}>
            {t("admin.groups.progress", { created: createdCount, total: missing })}
          </Typography>
        </Box>
      )}

      {/* Summary */}
      {selectedTemplateIds.size > 0 && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t("admin.groups.summary", { total, existing: total - missing, missing })}
        </Typography>
      )}

      {/* Group tree */}
      <Paper variant="outlined" sx={{ maxHeight: 500, overflow: "auto" }}>
        <List disablePadding dense>
          {resolvedTree.length > 0 ? (
            <GroupStatusRows statuses={resolvedTree} depth={0} />
          ) : (
            <ListItem>
              <ListItemText
                primary={t("admin.groups.noTemplatesSelected")}
                slotProps={{ primary: { color: "text.secondary" } }}
              />
            </ListItem>
          )}
        </List>
      </Paper>
    </Box>
  );
}
